import { CARRIER_RESOLUTION, formatParisDateTime, type OrderDetail } from '@flux/shared';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import type { JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { Badge, StatusBadge } from '@/components/ui/badge.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import { routerPath } from '../router-path.js';

type OrderAddress = NonNullable<OrderDetail['shippingAddress']>;
type OrderRelay = NonNullable<OrderDetail['relay']>;
type OrderCarrierDetail = NonNullable<OrderDetail['carrierDetail']>;

function AddressSection({
  title,
  address,
}: {
  title: string;
  address: OrderAddress | null;
}): JSX.Element {
  return (
    <PageSection title={title}>
      {address ? (
        <div className="flex flex-col gap-0.5 text-sm">
          {address.name ? <p className="font-medium">{address.name}</p> : null}
          {address.company ? <p>{address.company}</p> : null}
          {address.address1 ? <p>{address.address1}</p> : null}
          {address.address2 ? <p>{address.address2}</p> : null}
          <p>
            {[address.zip, address.city].filter(Boolean).join(' ')}
            {address.provinceCode ? ` (${address.provinceCode})` : ''}
          </p>
          {address.countryCode ? <p>{address.countryCode}</p> : null}
          {address.phone ? <p className="mt-1 text-muted-foreground">{address.phone}</p> : null}
          {address.email ? (
            <p className="break-words text-muted-foreground">{address.email}</p>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Non renseignée.</p>
      )}
    </PageSection>
  );
}

function RelayItem({ relay }: { relay: OrderRelay | null }): JSX.Element | null {
  if (!relay) {
    return null;
  }
  return (
    <DescriptionItem label="Point relais">
      <span className="flex flex-col gap-0.5">
        {relay.code ? <span className="font-medium">{relay.code}</span> : null}
        {relay.name ? <span>{relay.name}</span> : null}
        {relay.address1 ? <span>{relay.address1}</span> : null}
        {relay.address2 ? <span>{relay.address2}</span> : null}
        <span>
          {[relay.zip, relay.city].filter(Boolean).join(' ')} {relay.countryCode ?? ''}
        </span>
      </span>
    </DescriptionItem>
  );
}

function CarrierSection({
  carrier,
  relay,
}: {
  carrier: OrderCarrierDetail | null;
  relay: OrderRelay | null;
}): JSX.Element {
  const transporteursPath = routerPath('/transporteurs');
  return (
    <PageSection
      title="Transporteur"
      actions={
        carrier ? (
          <Link
            to={transporteursPath}
            search={{ tab: 'regles' as const }}
            className="rounded-sm text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Voir les règles transporteur
          </Link>
        ) : null
      }
    >
      {carrier || relay ? (
        <DescriptionList>
          {carrier ? (
            <>
              <DescriptionItem label="Transporteur">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span>
                    <span className="font-medium">{carrier.label}</span>{' '}
                    <span className="text-muted-foreground">({carrier.code})</span>
                  </span>
                  {carrier.pickup ? <Badge tone="blue">Point relais</Badge> : null}
                </span>
              </DescriptionItem>
              <DescriptionItem label="Obtenu par">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <StatusBadge registry={CARRIER_RESOLUTION} code={carrier.resolution} />
                  {carrier.ruleName === null ? null : (
                    <span className="text-muted-foreground">Règle « {carrier.ruleName} »</span>
                  )}
                </span>
              </DescriptionItem>
            </>
          ) : (
            <DescriptionItem label="Transporteur">
              <span className="text-muted-foreground">Aucun transporteur déterminé.</span>
            </DescriptionItem>
          )}
          <RelayItem relay={relay} />
        </DescriptionList>
      ) : (
        <p className="text-sm text-muted-foreground">Aucun transporteur déterminé.</p>
      )}
    </PageSection>
  );
}

/**
 * Expédition remontée par le Suivi Martelet (`02` §4) : date, transporteur et
 * n° de suivi, dès que la commande est expédiée. En simulation le suivi est
 * calculé mais rien n'a été écrit dans Shopify ; pendant la bascule, c'est la
 * v1 qui l'y a écrit (badge « V1 »).
 */
function ShippingSection({ order }: { order: OrderDetail }): JSX.Element | null {
  if (order.shippedAt === null) {
    return null;
  }
  const tracking = order.tracking;
  return (
    <PageSection
      title="Expédition"
      description={order.handledByV1?.shipping ? 'Expédition remontée dans Shopify par l’application Flux V1.' : undefined}
      actions={
        order.simulated ? <Badge tone="blue">Simulée</Badge> : order.handledByV1?.shipping ? <Badge tone="gray">V1</Badge> : undefined
      }
    >
      <DescriptionList>
        <DescriptionItem label="Expédiée le">
          <span className="tabular-nums">{formatParisDateTime(new Date(order.shippedAt))}</span>
        </DescriptionItem>
        <DescriptionItem label="Transporteur">{tracking?.company ?? '—'}</DescriptionItem>
        <DescriptionItem label="N° de suivi">
          {tracking?.number ? (
            tracking.url ? (
              <a
                href={tracking.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="font-mono">{tracking.number}</span>
                <ExternalLink className="size-3.5" aria-hidden="true" />
              </a>
            ) : (
              <span className="font-mono">{tracking.number}</span>
            )
          ) : (
            <span className="text-muted-foreground">Non communiqué par Martelet.</span>
          )}
        </DescriptionItem>
      </DescriptionList>
    </PageSection>
  );
}

/** Avertissements de la commande, en tête du Résumé — absents s'il n'y en a aucun. */
function WarningsSection({ warnings }: { warnings: OrderDetail['warnings'] }): JSX.Element | null {
  if (warnings.length === 0) {
    return null;
  }
  return (
    <PageSection title="Avertissements">
      <ul className="flex flex-col divide-y divide-border text-sm">
        {warnings.map((warning) => (
          <li
            key={`${warning.code}-${warning.message}`}
            className="flex items-start gap-2.5 py-2.5 first:pt-0 last:pb-0"
          >
            <AlertTriangle
              className="mt-0.5 size-4 shrink-0 text-status-orange-fg"
              aria-hidden="true"
            />
            <span className="min-w-0 break-words">
              {warning.field ? <span className="font-medium">{warning.field} : </span> : null}
              {warning.message}
            </span>
          </li>
        ))}
      </ul>
    </PageSection>
  );
}

/** Onglet 1 « Résumé » (`02` §4). */
export function SummaryTab({ order }: { order: OrderDetail }): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <WarningsSection warnings={order.warnings} />

      <div className="grid gap-6 lg:grid-cols-2">
        <AddressSection
          title="Adresse de livraison Shopify"
          address={order.shippingAddress}
        />
        <AddressSection
          title="Adresse de facturation Shopify"
          address={order.billingAddress}
        />
      </div>
      {order.addressChanged ? <p className="text-sm text-status-orange-fg"><AlertTriangle className="mr-2 inline size-4" />Les adresses Shopify ont changé entre la réception et la préparation. Les valeurs ci-dessus sont celles de la dernière préparation.</p> : null}

      <ShippingSection order={order} />

      <CarrierSection carrier={order.carrierDetail} relay={order.relay} />

      <PageSection title="Données Shopify">
        <DescriptionList>
          <DescriptionItem label="Tags">
            {order.tags.length > 0 ? (
              <span className="flex flex-wrap items-center gap-1.5">
                {order.tags.map((tag) => (
                  <Badge key={tag} tone="gray">
                    {tag}
                  </Badge>
                ))}
              </span>
            ) : (
              <span className="text-muted-foreground">aucun</span>
            )}
          </DescriptionItem>
        </DescriptionList>
      </PageSection>
    </div>
  );
}
